import { color } from 'console-log-colors'
import type { RouteLocation } from '#vue-router'
import type { PartialPharmacyParams } from '~/models/middleware-types'

type NamedRoute = RouteLocation['name']

/**
 * Ensures that the pharmacy params are valid
 * As long as id is valid, slug and name will be fixed if they are not
 * Will be navigated to correct pharmacy url
 */
export default defineNuxtRouteMiddleware(async (to) => {
  onShowLogs(() => console.info(color.blueBright('[02] pharmacy-routing\n'), to))

  const { validateIncomingPharmacy } = useRoutingValidator()
  const router = useRouter()

  const {
    pharmacyName,
    pharmacyPlz,
    pharmacyId,
  } = to.params as PartialPharmacyParams

  // pharmacyName is sometimes an array
  const pharmacyParams = {
    pharmacyName: Array.isArray(pharmacyName) ? pharmacyName.at(0) : pharmacyName,
    pharmacyPlz,
    pharmacyId,
  }

  if (!pharmacyId) {
    return
  }

  // Handle short pharmacy routes: /apotheke/[pharmacyId]
  // This is not a recommended way of opening pharmacy page and it's only for internal use
  const shortPharmacyPageRoutes: NamedRoute[] = ['apotheke-pharmacyId-short', 'pharmacy-pharmacyId-short']

  const targetRouteName = shortPharmacyPageRoutes.flat().includes(to.name)
    ? 'apotheke-pharmacyName-pharmacyPlz-pharmacyId'
    : to.name

  const pharmacyValidated = await validateIncomingPharmacy(pharmacyParams)
  switch (pharmacyValidated.status) {
    case 200: {
      if (isClient) {
        proceedToPharmacySwitchCheck(pharmacyParams.pharmacyId)
      }
      break
    }
    // handles routes with mismatched pharmacy url, or short pharmacy routes
    case 301: {
      let correctPharmacyRoute = handleShortPharmacyPages(pharmacyValidated, to)

      if (!correctPharmacyRoute) {
        // Address-less pharmacies have no full slug URL, so fall back to
        // the id-only short pharmacy route
        const correctRouteName = hasFullPharmacySlug(pharmacyValidated.params ?? {})
          ? targetRouteName
          : 'apotheke-pharmacyId-short'

        correctPharmacyRoute = router.resolve({
          name: correctRouteName,
          params: pharmacyValidated.params,
          query: to.query,
          hash: to.hash,
        })
      }

      return customNavigateTo(correctPharmacyRoute, { redirectCode: 301 })
    }
    // Handles deactivated pharmacies, with or without mismatched pharmacy url, or short pharmacy routes
    case 302: {
      // Each pharmacy page has nonPharmacyRoute meta field that represents same page without pharmacy
      // If we don't want this, we could also just extend the pharmacyRelatedPages map in 02.pharmacy-related.ts middleware
      // mapping the pharmacy page to the non-pharmacy page, extract it, and use in both places
      const nonPharmacyRoute = to.meta.nonPharmacyRoute as string | undefined
      // Apofinder redirects to homepage by default, so returnUrl is not needed in that case
      const returnUrl = (nonPharmacyRoute && nonPharmacyRoute !== 'index') ? nonPharmacyRoute : undefined

      const correctPharmacyRoute = router.resolve({
        name: pharmacyValidated.name!,
        query: {
          returnUrl,
        },
      })

      return customNavigateTo(correctPharmacyRoute, { redirectCode: 302 })
    }
    default:
      return abortNavigation({ statusCode: pharmacyValidated.status })
  }
})
